/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  filter as filter_,
  reatomBoolean,
  withAssign,
} from '@reatom/framework'

import { BigNumber as BigNumber_, Order } from '@blade/std-lib'
import { reatomNumericInput, reatomStringArrayInput } from '@root/shared/lib'

import * as ExploreFilter from '../model/explore-filter'

import type { ExploreFiltersStateModel } from './reatom-explore-filters-state'
import type { Provider } from '@root/entities/provider'

const isRangeExceeded = <T>(compare: Order.Order<T>) => {
  const gt = Order.isGreaterThan(compare)
  return (left: T | undefined, right: T | undefined) => {
    if (left !== undefined && right !== undefined) {
      return gt(left, right)
    }

    return false
  }
}

const isRangeExceededBigNumber = isRangeExceeded(BigNumber_.order)

export const reatomExploreFiltersForm = (
  options: {
    state: ExploreFiltersStateModel
  },
  name: string,
) => {
  const { state } = options
  // WithInit causes stack overflow
  const initialFilters = state.initialFilters

  const liquidityMinAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.minLiquidityInUsd,
      optional: true,
    },
    `${name}.liquidityMinAtom`,
  )
  const liquidityMaxAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.maxLiquidityInUsd,
      optional: true,
    },
    `${name}.liquidityMaxAtom`,
  )

  const volumeMinAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.minVolumeInUsd,
      optional: true,
    },
    `${name}.volumeMinAtom`,
  )
  const volumeMaxAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.maxVolumeInUsd,
      optional: true,
    },
    `${name}.volumeMaxAtom`,
  )
  const marketCapMinAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.minMarketCap,
      optional: true,
    },
    `${name}.marketCapMinAtom`,
  )
  const marketCapMaxAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.maxMarketCap,
      optional: true,
    },
    `${name}.marketCapMaxAtom`,
  )

  const minAgeAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.minAge,
      optional: true,
    },
    `${name}.minAgeAtom`,
  )
  const maxAgeAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.maxAge,
      optional: true,
    },
    `${name}.maxAgeAtom`,
  )

  const keywordsAtom = reatomStringArrayInput(
    {
      initial: initialFilters.keywords.join(','),
      joiner: ',',
    },
    `${name}.keywordsAtom`,
  )

  const holdersMinAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.minHolders,
      optional: true,
    },
    `${name}.holdersMinAtom`,
  )
  const holdersMaxAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.maxHolders,
      optional: true,
    },
    `${name}.holdersMaxAtom`,
  )
  const txMinAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.minTx,
      optional: true,
    },
    `${name}.txMinAtom`,
  )
  const txMaxAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.maxTx,
      optional: true,
    },
    `${name}.txMaxAtom`,
  )
  const boostersMinAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.minBoosters,
      optional: true,
    },
    `${name}.boostersMinAtom`,
  )
  const boostersMaxAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.maxBoosters,
      optional: true,
    },
    `${name}.boostersMaxAtom`,
  )
  const splashPointsMinAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.minSplashPoints,
      optional: true,
    },
    `${name}.splashPointsMinAtom`,
  )
  const splashPointsMaxAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.maxSplashPoints,
      optional: true,
    },
    `${name}.splashPointsMaxAtom`,
  )
  const minDevHoldingAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.minDevHolding,
      optional: true,
    },
    `${name}.splashPointsMinAtom`,
  )
  const maxDevHoldingAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.maxDevHolding,
      optional: true,
    },
    `${name}.splashPointsMaxAtom`,
  )
  const minTop10HoldingAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.minTop10Holding,
      optional: true,
    },
    `${name}.minTop10HoldingAtom`,
  )
  const maxTop10HoldingAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.maxTop10Holding,
      optional: true,
    },
    `${name}.maxTop10HoldingAtom`,
  )
  const minInsidersHoldingAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.minInsidersHolding,
      optional: true,
    },
    `${name}.minInsidersHoldingAtom`,
  )
  const maxInsidersHoldingAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.maxInsidersHolding,
      optional: true,
    },
    `${name}.maxInsidersHoldingAtom`,
  )
  const minSnipersHoldingAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.minSnipersHolding,
      optional: true,
    },
    `${name}.minSnipersHoldingAtom`,
  )
  const maxSnipersHoldingAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.maxSnipersHolding,
      optional: true,
    },
    `${name}.maxSnipersHoldingAtom`,
  )
  const minBundlersHoldingAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.minBundlersHolding,
      optional: true,
    },
    `${name}.minBundlersHoldingAtom`,
  )
  const maxBundlersHoldingAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.maxBundlersHolding,
      optional: true,
    },
    `${name}.maxBundlersHoldingAtom`,
  )

  const minFeeAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.minFee,
      optional: true,
    },
    `${name}.minFeeAtom`,
  )
  const maxFeeAtom = reatomNumericInput(
    {
      initialAmount: initialFilters.maxFee,
      optional: true,
    },
    `${name}.maxFeeAtom`,
  )
  const providersAtom = atom<Set<Provider.Id> | 'all'>(initialFilters.providers)
  const withMayhemAtom = reatomBoolean(initialFilters.withMayhem)
  const withAtLeastOneSocialAtom = reatomBoolean(
    initialFilters.withAtLeastOneSocial,
  )
  const withTwitterAtom = reatomBoolean(initialFilters.withTwitter)
  const withTelegramAtom = reatomBoolean(initialFilters.withTelegram)
  const withWebsiteAtom = reatomBoolean(initialFilters.withWebsite)

  const filtersCombinedAtom = atom((ctx) => {
    const liquidityMin = ctx.spy(liquidityMinAtom)
    const liquidityMax = ctx.spy(liquidityMaxAtom)
    const volumeMin = ctx.spy(volumeMinAtom)
    const volumeMax = ctx.spy(volumeMaxAtom)
    const marketCapMin = ctx.spy(marketCapMinAtom)
    const marketCapMax = ctx.spy(marketCapMaxAtom)
    const boostersMax = ctx.spy(boostersMaxAtom)
    const boostersMin = ctx.spy(boostersMinAtom)
    const splashPointsMax = ctx.spy(splashPointsMaxAtom)
    const splashPointsMin = ctx.spy(splashPointsMinAtom)
    const minDevHolding = ctx.spy(minDevHoldingAtom)
    const maxDevHolding = ctx.spy(maxDevHoldingAtom)
    const minTop10Holding = ctx.spy(minTop10HoldingAtom)
    const maxTop10Holding = ctx.spy(maxTop10HoldingAtom)
    const minInsidersHolding = ctx.spy(minInsidersHoldingAtom)
    const maxInsidersHolding = ctx.spy(maxInsidersHoldingAtom)
    const minSnipersHolding = ctx.spy(minSnipersHoldingAtom)
    const maxSnipersHolding = ctx.spy(maxSnipersHoldingAtom)
    const minBundlersHolding = ctx.spy(minBundlersHoldingAtom)
    const maxBundlersHolding = ctx.spy(maxBundlersHoldingAtom)
    const keywords = ctx.spy(keywordsAtom)
    const minHolders = ctx.spy(holdersMinAtom)
    const maxHolders = ctx.spy(holdersMaxAtom)
    const minTx = ctx.spy(txMinAtom)
    const maxTx = ctx.spy(txMaxAtom)
    const minAge = ctx.spy(minAgeAtom)
    const maxAge = ctx.spy(maxAgeAtom)
    const providers = ctx.spy(providersAtom)
    const withMayhem = ctx.spy(withMayhemAtom)
    const minFee = ctx.spy(minFeeAtom)
    const maxFee = ctx.spy(maxFeeAtom)
    const withAtLeastOneSocial = ctx.spy(withAtLeastOneSocialAtom) || undefined
    const withTwitter = ctx.spy(withTwitterAtom) || undefined
    const withTelegram = ctx.spy(withTelegramAtom) || undefined
    const withWebsite = ctx.spy(withWebsiteAtom) || undefined

    const filters: ExploreFilter.ExploreFilter = {
      keywords,
      minHolders: minHolders,
      maxHolders: maxHolders,
      minTx: minTx,
      maxTx: maxTx,
      maxLiquidityInUsd: liquidityMax,
      minLiquidityInUsd: liquidityMin,
      minVolumeInUsd: volumeMin,
      maxVolumeInUsd: volumeMax,
      minMarketCap: marketCapMin,
      maxMarketCap: marketCapMax,
      maxBoosters: boostersMax,
      minBoosters: boostersMin,
      maxSplashPoints: splashPointsMax,
      minSplashPoints: splashPointsMin,
      minDevHolding: minDevHolding,
      maxDevHolding: maxDevHolding,
      minTop10Holding: minTop10Holding,
      maxTop10Holding: maxTop10Holding,
      minInsidersHolding: minInsidersHolding,
      maxInsidersHolding: maxInsidersHolding,
      minSnipersHolding: minSnipersHolding,
      maxSnipersHolding: maxSnipersHolding,
      minBundlersHolding: minBundlersHolding,
      maxBundlersHolding: maxBundlersHolding,
      providers: providers,
      withMayhem: withMayhem,
      minAge: minAge?.toNumber(),
      maxAge: maxAge?.toNumber(),
      minFee: minFee,
      maxFee: maxFee,
      withAtLeastOneSocial,
      withTwitter,
      withTelegram,
      withWebsite,
    }

    return filters
  }, `${name}.filtersCombinedAtom`).pipe(
    filter_((ctx, a, b) => !ExploreFilter.eq(a, b)),
  )

  const isDirtyAtom = atom((ctx) => {
    const filters = ctx.spy(state)
    const formValues = ctx.spy(filtersCombinedAtom)

    return !ExploreFilter.eq(filters, formValues)
  })

  const isEmptyAtom = atom((ctx) => {
    const formValues = ctx.spy(filtersCombinedAtom)

    return ExploreFilter.eq(ExploreFilter.EMPTY, formValues)
  })

  const filtersEditState = atom((ctx) => {
    const liquidityMin = ctx.spy(liquidityMinAtom)
    const liquidityMax = ctx.spy(liquidityMaxAtom)
    const volumeMin = ctx.spy(volumeMinAtom)
    const volumeMax = ctx.spy(volumeMaxAtom)
    const marketCapMin = ctx.spy(marketCapMinAtom)
    const marketCapMax = ctx.spy(marketCapMaxAtom)
    const holdersMin = ctx.spy(holdersMinAtom)
    const holdersMax = ctx.spy(holdersMaxAtom)
    const txMin = ctx.spy(txMinAtom)
    const txMax = ctx.spy(txMaxAtom)
    const boostersMin = ctx.spy(boostersMinAtom)
    const boostersMax = ctx.spy(boostersMaxAtom)
    const splashPointsMin = ctx.spy(splashPointsMinAtom)
    const splashPointsMax = ctx.spy(splashPointsMaxAtom)
    const minDevHolding = ctx.spy(minDevHoldingAtom)
    const maxDevHolding = ctx.spy(maxDevHoldingAtom)
    const minTop10Holding = ctx.spy(minTop10HoldingAtom)
    const maxTop10Holding = ctx.spy(maxTop10HoldingAtom)
    const minInsidersHolding = ctx.spy(minInsidersHoldingAtom)
    const maxInsidersHolding = ctx.spy(maxInsidersHoldingAtom)
    const minSnipersHolding = ctx.spy(minSnipersHoldingAtom)
    const maxSnipersHolding = ctx.spy(maxSnipersHoldingAtom)
    const minBundlersHolding = ctx.spy(minBundlersHoldingAtom)
    const maxBundlersHolding = ctx.spy(maxBundlersHoldingAtom)
    const maxAge = ctx.spy(maxAgeAtom)
    const minAge = ctx.spy(minAgeAtom)
    const minFee = ctx.spy(minFeeAtom)
    const maxFee = ctx.spy(maxFeeAtom)

    const isEmpty = ctx.spy(isEmptyAtom)
    const isChanged = ctx.spy(isDirtyAtom)

    const isRangeExceeded =
      isRangeExceededBigNumber(liquidityMin, liquidityMax) ||
      isRangeExceededBigNumber(volumeMin, volumeMax) ||
      isRangeExceededBigNumber(holdersMin, holdersMax) ||
      isRangeExceededBigNumber(txMin, txMax) ||
      isRangeExceededBigNumber(marketCapMin, marketCapMax) ||
      isRangeExceededBigNumber(boostersMin, boostersMax) ||
      isRangeExceededBigNumber(splashPointsMin, splashPointsMax) ||
      isRangeExceededBigNumber(minDevHolding, maxDevHolding) ||
      isRangeExceededBigNumber(minTop10Holding, maxTop10Holding) ||
      isRangeExceededBigNumber(minInsidersHolding, maxInsidersHolding) ||
      isRangeExceededBigNumber(minSnipersHolding, maxSnipersHolding) ||
      isRangeExceededBigNumber(minBundlersHolding, maxBundlersHolding) ||
      isRangeExceededBigNumber(minAge, maxAge) ||
      isRangeExceededBigNumber(minFee, maxFee)

    return { isEmpty, isChanged, isRangeExceeded }
  }, `${name}.filtersEditState`)

  const applyFiltersAction = action((ctx) => {
    const filters = ctx.get(filtersCombinedAtom)
    state.set(ctx, filters)
  }, `${name}.applyFiltersAction`)

  const resetFiltersAction = action((ctx) => {
    liquidityMinAtom.setRaw(ctx, '')
    liquidityMaxAtom.setRaw(ctx, '')
    holdersMinAtom.setRaw(ctx, '')
    holdersMaxAtom.setRaw(ctx, '')
    txMinAtom.setRaw(ctx, '')
    txMaxAtom.setRaw(ctx, '')
    volumeMinAtom.setRaw(ctx, '')
    volumeMaxAtom.setRaw(ctx, '')
    marketCapMinAtom.setRaw(ctx, '')
    marketCapMaxAtom.setRaw(ctx, '')
    keywordsAtom.setRaw(ctx, '')
    boostersMaxAtom.setRaw(ctx, '')
    boostersMinAtom.setRaw(ctx, '')
    splashPointsMaxAtom.setRaw(ctx, '')
    splashPointsMinAtom.setRaw(ctx, '')
    minDevHoldingAtom.setRaw(ctx, '')
    maxDevHoldingAtom.setRaw(ctx, '')
    minTop10HoldingAtom.setRaw(ctx, '')
    maxTop10HoldingAtom.setRaw(ctx, '')
    minInsidersHoldingAtom.setRaw(ctx, '')
    maxInsidersHoldingAtom.setRaw(ctx, '')
    minSnipersHoldingAtom.setRaw(ctx, '')
    maxSnipersHoldingAtom.setRaw(ctx, '')
    minBundlersHoldingAtom.setRaw(ctx, '')
    maxBundlersHoldingAtom.setRaw(ctx, '')
    minAgeAtom.setRaw(ctx, '')
    maxAgeAtom.setRaw(ctx, '')
    minFeeAtom.setRaw(ctx, '')
    maxFeeAtom.setRaw(ctx, '')
    withTwitterAtom.setFalse(ctx)
    withTelegramAtom.setFalse(ctx)
    withWebsiteAtom.setFalse(ctx)
    withAtLeastOneSocialAtom.setFalse(ctx)
    providersAtom(ctx, 'all')
    withMayhemAtom.setFalse(ctx)

    state.reset(ctx)
  }, `${name}.resetFiltersAction`)

  return filtersCombinedAtom.pipe(
    withAssign(() => ({
      applyFilters: applyFiltersAction,
      resetFilters: resetFiltersAction,
      filtersEditState,
      filter: filtersCombinedAtom,

      keywords: keywordsAtom,
      maxLiquidityInUsd: liquidityMaxAtom,
      minLiquidityInUsd: liquidityMinAtom,
      maxVolumeInUsd: volumeMaxAtom,
      minVolumeInUsd: volumeMinAtom,
      maxMarketCap: marketCapMaxAtom,
      minMarketCap: marketCapMinAtom,
      minFee: minFeeAtom,
      maxFee: maxFeeAtom,
      maxHolders: holdersMaxAtom,
      minHolders: holdersMinAtom,
      maxTx: txMaxAtom,
      minTx: txMinAtom,
      maxBoosters: boostersMaxAtom,
      minBoosters: boostersMinAtom,
      maxSplashPoints: splashPointsMaxAtom,
      minSplashPoints: splashPointsMinAtom,
      minAge: minAgeAtom,
      maxAge: maxAgeAtom,
      minDevHolding: minDevHoldingAtom,
      maxDevHolding: maxDevHoldingAtom,
      minTop10Holding: minTop10HoldingAtom,
      maxTop10Holding: maxTop10HoldingAtom,
      minInsidersHolding: minInsidersHoldingAtom,
      maxInsidersHolding: maxInsidersHoldingAtom,
      minSnipersHolding: minSnipersHoldingAtom,
      maxSnipersHolding: maxSnipersHoldingAtom,
      minBundlersHolding: minBundlersHoldingAtom,
      maxBundlersHolding: maxBundlersHoldingAtom,
      providers: providersAtom,
      withMayhem: withMayhemAtom,
      withAtLeastOneSocial: withAtLeastOneSocialAtom,
      withTwitter: withTwitterAtom,
      withTelegram: withTelegramAtom,
      withWebsite: withWebsiteAtom,
    })),
  )
}

export type ExploreFiltersFormModel = ReturnType<
  typeof reatomExploreFiltersForm
>
